import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, UploadIcon, FileIcon, CheckCircleIcon, AlertCircleIcon, FilterIcon, FileDownIcon, CheckIcon, XIcon } from 'lucide-react';
interface MatchResult {
  ticketNumber: string;
  matched: boolean;
  systemAmount: number;
  billingAmount: number;
  status: 'matched' | 'mismatch' | 'unmatched';
}
const BillingUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'matched' | 'mismatch' | 'unmatched'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Mock data for demonstration
  const mockMatchResults: MatchResult[] = [{
    ticketNumber: '1601234567890',
    matched: true,
    systemAmount: 1500.0,
    billingAmount: 1500.0,
    status: 'matched'
  }, {
    ticketNumber: '1601234567891',
    matched: false,
    systemAmount: 1200.0,
    billingAmount: 1250.0,
    status: 'mismatch'
  }, {
    ticketNumber: '1601234567892',
    matched: false,
    systemAmount: 1800.0,
    billingAmount: 0,
    status: 'unmatched'
  }, {
    ticketNumber: '1601234567893',
    matched: true,
    systemAmount: 950.0,
    billingAmount: 950.0,
    status: 'matched'
  }, {
    ticketNumber: '1601234567894',
    matched: false,
    systemAmount: 1350.0,
    billingAmount: 1400.0,
    status: 'mismatch'
  }];
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFile(files[0]);
    }
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };
  const handleFile = (file: File) => {
    // Check if file type is valid (Excel, CSV, PDF)
    const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid Excel, CSV, or PDF file');
      return;
    }
    setFile(file);
    setUploadStatus('uploading');
    // Simulate upload progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setUploadStatus('success');
        // Simulate processing delay
        setTimeout(() => {
          setMatchResults(mockMatchResults);
        }, 500);
      }
    }, 300);
  };
  const filteredResults = matchResults.filter(result => {
    if (activeFilter === 'all') return true;
    return result.status === activeFilter;
  });
  const summary = {
    total: matchResults.length,
    matched: matchResults.filter(r => r.status === 'matched').length,
    mismatch: matchResults.filter(r => r.status === 'mismatch').length,
    unmatched: matchResults.filter(r => r.status === 'unmatched').length
  };
  const handleMarkAsReviewed = () => {
    console.log('Marking mismatches as reviewed');
    // Implement mark as reviewed functionality
  };
  const handleExportMismatches = () => {
    console.log('Exporting mismatches');
    // Implement export functionality
  };
  const renderUploadState = () => {
    switch (uploadStatus) {
      case 'idle':
        return <div className={`border-2 border-dashed rounded-lg p-12 text-center ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-1256">
            <div className="flex flex-col items-center" data-id="element-1257">
              <UploadIcon className="h-12 w-12 text-gray-400 mb-4" data-id="element-1258" />
              <p className="text-lg font-medium text-gray-900 mb-1" data-id="element-1259">
                Drag and drop your file here
              </p>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1260">
                Upload a billing file in Excel, CSV, or PDF format
              </p>
              <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => fileInputRef.current?.click()} data-id="element-1261">
                Browse Files
              </button>
              <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" onChange={handleFileInput} data-id="element-1262" />
            </div>
          </div>;
      case 'uploading':
        return <div className="border rounded-lg p-6" data-id="element-1263">
            <div className="flex items-center mb-4" data-id="element-1264">
              <FileIcon className="h-8 w-8 text-blue-500 mr-3" data-id="element-1265" />
              <div className="flex-1" data-id="element-1266">
                <p className="text-sm font-medium text-gray-900" data-id="element-1267">
                  {file?.name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1268">
                  {file?.size ? `${(file.size / 1024).toFixed(2)} KB` : ''}
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1269">
              <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
              width: `${uploadProgress}%`
            }} data-id="element-1270"></div>
            </div>
            <p className="text-sm text-gray-500 mt-2" data-id="element-1271">
              Uploading... {uploadProgress}%
            </p>
          </div>;
      case 'success':
        return <div className="border border-green-200 bg-green-50 rounded-lg p-6" data-id="element-1272">
            <div className="flex items-center" data-id="element-1273">
              <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" data-id="element-1274" />
              <div data-id="element-1275">
                <p className="text-sm font-medium text-gray-900" data-id="element-1276">
                  Upload successful
                </p>
                <p className="text-xs text-gray-500" data-id="element-1277">{file?.name}</p>
              </div>
              <button className="ml-auto text-sm text-blue-600 hover:text-blue-800" onClick={() => {
              setFile(null);
              setUploadStatus('idle');
              setUploadProgress(0);
            }} data-id="element-1278">
                Upload Another
              </button>
            </div>
          </div>;
      case 'error':
        return <div className="border border-red-200 bg-red-50 rounded-lg p-6" data-id="element-1279">
            <div className="flex items-center" data-id="element-1280">
              <AlertCircleIcon className="h-8 w-8 text-red-500 mr-3" data-id="element-1281" />
              <div data-id="element-1282">
                <p className="text-sm font-medium text-gray-900" data-id="element-1283">
                  Upload failed
                </p>
                <p className="text-xs text-gray-500" data-id="element-1284">Please try again</p>
              </div>
              <button className="ml-auto text-sm text-blue-600 hover:text-blue-800" onClick={() => {
              setFile(null);
              setUploadStatus('idle');
              setUploadProgress(0);
            }} data-id="element-1285">
                Try Again
              </button>
            </div>
          </div>;
    }
  };
  return <div className="space-y-6" data-id="element-1286">
      <div className="flex items-center gap-4" data-id="element-1287">
        <Link to="/" className="text-gray-500 hover:text-gray-700" data-id="element-1288">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-1289" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1290">
          Billing Upload & Reconciliation
        </h1>
      </div>
      <div className="bg-white shadow rounded-lg p-6" data-id="element-1291">
        <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1292">
          Upload Billing File
        </h2>
        {renderUploadState()}
      </div>
      {matchResults.length > 0 && <>
          <div className="bg-white shadow rounded-lg p-6" data-id="element-1293">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1294">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-1295">
                Reconciliation Results
              </h2>
              <div className="flex flex-wrap gap-2" data-id="element-1296">
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handleMarkAsReviewed} data-id="element-1297">
                  <CheckIcon className="h-5 w-5 mr-1" data-id="element-1298" />
                  <span data-id="element-1299">Mark as Reviewed</span>
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handleExportMismatches} data-id="element-1300">
                  <FileDownIcon className="h-5 w-5 mr-1" data-id="element-1301" />
                  <span data-id="element-1302">Export Mismatches</span>
                </button>
              </div>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1303">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1304">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1305">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1306">
                  {summary.total}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1307">
                <p className="text-sm text-green-700 mb-1" data-id="element-1308">Matched</p>
                <p className="text-2xl font-bold text-green-800" data-id="element-1309">
                  {summary.matched}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200" data-id="element-1310">
                <p className="text-sm text-red-700 mb-1" data-id="element-1311">Mismatched</p>
                <p className="text-2xl font-bold text-red-800" data-id="element-1312">
                  {summary.mismatch}
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200" data-id="element-1313">
                <p className="text-sm text-yellow-700 mb-1" data-id="element-1314">Unmatched</p>
                <p className="text-2xl font-bold text-yellow-800" data-id="element-1315">
                  {summary.unmatched}
                </p>
              </div>
            </div>
            {/* Filter Tabs */}
            <div className="border-b border-gray-200 mb-4" data-id="element-1316">
              <nav className="-mb-px flex space-x-8" data-id="element-1317">
                <button onClick={() => setActiveFilter('all')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeFilter === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1318">
                  All ({summary.total})
                </button>
                <button onClick={() => setActiveFilter('matched')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeFilter === 'matched' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1319">
                  Matched ({summary.matched})
                </button>
                <button onClick={() => setActiveFilter('mismatch')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeFilter === 'mismatch' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1320">
                  Mismatched ({summary.mismatch})
                </button>
                <button onClick={() => setActiveFilter('unmatched')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeFilter === 'unmatched' ? 'border-yellow-500 text-yellow-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1321">
                  Unmatched ({summary.unmatched})
                </button>
              </nav>
            </div>
            {/* Results Table */}
            <div className="overflow-x-auto" data-id="element-1322">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1323">
                <thead className="bg-gray-50" data-id="element-1324">
                  <tr data-id="element-1325">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1326">
                      Ticket Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1327">
                      Matched
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1328">
                      System Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1329">
                      Billing Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1330">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1331">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1332">
                  {filteredResults.map((result, index) => <tr key={result.ticketNumber} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`} data-id="element-1333">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1334">
                        {result.ticketNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1335">
                        {result.matched ? <span className="inline-flex items-center text-green-800" data-id="element-1336">
                            <CheckIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-1337" />{' '}
                            Yes
                          </span> : <span className="inline-flex items-center text-red-800" data-id="element-1338">
                            <XIcon className="h-4 w-4 mr-1 text-red-500" data-id="element-1339" /> No
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1340">
                        ${result.systemAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1341">
                        ${result.billingAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1342">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${result.status === 'matched' ? 'bg-green-100 text-green-800' : result.status === 'mismatch' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1343">
                          {result.status === 'matched' && 'Matched'}
                          {result.status === 'mismatch' && 'Mismatch'}
                          {result.status === 'unmatched' && 'Unmatched'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1344">
                        <button className="text-blue-600 hover:text-blue-900 mr-2" data-id="element-1345">
                          View
                        </button>
                        {result.status !== 'matched' && <button className="text-blue-600 hover:text-blue-900" data-id="element-1346">
                            Edit
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              {filteredResults.length === 0 && <div className="py-8 text-center" data-id="element-1347">
                  <p className="text-gray-500" data-id="element-1348">
                    No records found matching the selected filter.
                  </p>
                </div>}
            </div>
          </div>
        </>}
    </div>;
};
export default BillingUpload;